import React, { useState, useEffect } from 'react';
import { SOPService } from '../../services/sopApi';
import { AIInsight, InsightType, SeverityLevel, InsightsRequest, InsightStatus } from '../../types/sop';
import LoadingIndicator from '../common/LoadingIndicator';
import ErrorDisplay from '../common/ErrorDisplay';
import './InsightsDashboard.css';

const InsightsDashboard: React.FC = () => {
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [filteredInsights, setFilteredInsights] = useState<AIInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    severity: [] as SeverityLevel[],
    type: [] as InsightType[],
    warehouse: [] as string[],
    status: 'all' as 'all' | 'new' | 'acknowledged' | 'resolved'
  });
  const [warehouses, setWarehouses] = useState<string[]>([]);

  useEffect(() => {
    loadInsights();
    loadWarehouses();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [insights, filters]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const request: InsightsRequest = {
        limit: 100
      };
      
      const insightsData = await SOPService.getInsights(request);
      setInsights(insightsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const warehouseList = await SOPService.getWarehouses();
      setWarehouses(warehouseList);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...insights];

    // Filter by severity
    if (filters.severity.length > 0) {
      filtered = filtered.filter(insight => filters.severity.includes(insight.severity));
    }

    // Filter by type
    if (filters.type.length > 0) {
      filtered = filtered.filter(insight => filters.type.includes(insight.insight_type));
    }

    // Filter by warehouse
    if (filters.warehouse.length > 0) {
      filtered = filtered.filter(insight => 
        insight.affected_warehouses.some(wh => filters.warehouse.includes(wh))
      );
    }

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(insight => insight.status === filters.status);
    }

    setFilteredInsights(filtered);
  };

  const handleFilterChange = (filterType: keyof typeof filters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleMultiSelectFilter = (filterType: 'severity' | 'type' | 'warehouse', value: string) => {
    setFilters(prev => {
      const currentFilter = prev[filterType] as string[];
      return {
        ...prev,
        [filterType]: currentFilter.includes(value)
          ? currentFilter.filter(item => item !== value)
          : [...currentFilter, value]
      };
    });
  };

  const acknowledgeInsight = async (insightId: string) => {
    try {
      // In real implementation, this would call the API
      setInsights(prev => prev.map(insight => 
        insight.insight_id === insightId 
          ? { ...insight, status: InsightStatus.ACKNOWLEDGED }
          : insight
      ));
    } catch (err) {
      setError('Failed to acknowledge insight');
    }
  };

  const resolveInsight = async (insightId: string) => {
    try {
      // In real implementation, this would call the API
      setInsights(prev => prev.map(insight => 
        insight.insight_id === insightId 
          ? { ...insight, status: InsightStatus.RESOLVED }
          : insight
      ));
    } catch (err) {
      setError('Failed to resolve insight');
    }
  };

  const getSeverityColor = (severity: SeverityLevel) => {
    const colors = {
      low: 'var(--color-success)',
      medium: 'var(--color-warning)',
      high: 'var(--color-error)',
      critical: 'var(--color-error-dark)'
    };
    return colors[severity];
  };

  const getTypeIcon = (type: InsightType) => {
    const icons = {
      alert: '⚠️',
      recommendation: '💡',
      trend: '📈'
    };
    return icons[type];
  };

  const getInsightSummary = () => {
    const summary = {
      total: insights.length,
      new: insights.filter(i => i.status === InsightStatus.NEW).length,
      critical: insights.filter(i => i.severity === SeverityLevel.CRITICAL).length,
      high: insights.filter(i => i.severity === SeverityLevel.HIGH).length
    };
    return summary;
  };

  const summary = getInsightSummary();

  if (loading) {
    return <LoadingIndicator message="Loading AI insights..." />;
  }

  if (error) {
    return (
      <ErrorDisplay 
        error={error} 
        onRetry={loadInsights}
        operation="Insights Dashboard"
      />
    );
  }

  return (
    <div className="insights-dashboard">
      <div className="insights-header">
        <h2>AI Insights Dashboard</h2>
        <p>Real-time alerts, recommendations, and trend analysis for your S&OP planning</p>
      </div>

      <div className="insights-summary">
        <div className="summary-card">
          <div className="summary-number">{summary.total}</div>
          <div className="summary-label">Total Insights</div>
        </div>
        <div className="summary-card new">
          <div className="summary-number">{summary.new}</div>
          <div className="summary-label">New Alerts</div>
        </div>
        <div className="summary-card critical">
          <div className="summary-number">{summary.critical}</div>
          <div className="summary-label">Critical Issues</div>
        </div>
        <div className="summary-card high">
          <div className="summary-number">{summary.high}</div>
          <div className="summary-label">High Priority</div>
        </div>
      </div>

      <div className="insights-content">
        <div className="insights-filters">
          <h3>Filters</h3>
          
          <div className="filter-group">
            <h4>Severity</h4>
            {Object.values(SeverityLevel).map(severity => (
              <label key={severity} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.severity.includes(severity)}
                  onChange={() => handleMultiSelectFilter('severity', severity)}
                />
                <span style={{ color: getSeverityColor(severity) }}>
                  {severity.charAt(0).toUpperCase() + severity.slice(1)}
                </span>
              </label>
            ))}
          </div>

          <div className="filter-group">
            <h4>Type</h4>
            {Object.values(InsightType).map(type => (
              <label key={type} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.type.includes(type)}
                  onChange={() => handleMultiSelectFilter('type', type)}
                />
                <span>
                  {getTypeIcon(type)} {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
              </label>
            ))}
          </div>

          <div className="filter-group">
            <h4>Warehouse</h4>
            {warehouses.map(warehouse => (
              <label key={warehouse} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.warehouse.includes(warehouse)}
                  onChange={() => handleMultiSelectFilter('warehouse', warehouse)}
                />
                <span>{warehouse}</span>
              </label>
            ))}
          </div>

          <div className="filter-group">
            <h4>Status</h4>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="form-select"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          <button 
            onClick={() => setFilters({
              severity: [],
              type: [],
              warehouse: [],
              status: 'all'
            })}
            className="btn btn-secondary btn-sm"
          >
            Clear Filters
          </button>
        </div>

        <div className="insights-list">
          {filteredInsights.length === 0 ? (
            <div className="no-insights">
              <h3>No insights match your filters</h3>
              <p>Try adjusting your filter criteria or refresh the data</p>
            </div>
          ) : (
            filteredInsights.map(insight => (
              <div 
                key={insight.insight_id} 
                className={`insight-card ${insight.severity} ${insight.status}`}
              >
                <div className="insight-header">
                  <div className="insight-meta">
                    <span className="insight-type">
                      {getTypeIcon(insight.insight_type)} {insight.insight_type}
                    </span>
                    <span 
                      className="insight-severity"
                      style={{ color: getSeverityColor(insight.severity) }}
                    >
                      {insight.severity.toUpperCase()}
                    </span>
                    <span className="insight-confidence">
                      {(insight.ai_confidence * 100).toFixed(0)}% confidence
                    </span>
                  </div>
                  <div className="insight-actions">
                    {insight.status === InsightStatus.NEW && (
                      <button
                        onClick={() => acknowledgeInsight(insight.insight_id)}
                        className="btn btn-sm btn-secondary"
                      >
                        Acknowledge
                      </button>
                    )}
                    {insight.status === InsightStatus.ACKNOWLEDGED && (
                      <button
                        onClick={() => resolveInsight(insight.insight_id)}
                        className="btn btn-sm btn-primary"
                      >
                        Resolve
                      </button>
                    )}
                  </div>
                </div>

                <div className="insight-content">
                  <h4>{insight.title}</h4>
                  <p>{insight.description}</p>

                  <div className="insight-details">
                    <div className="affected-items">
                      <strong>Affected:</strong>
                      <div className="affected-tags">
                        {insight.affected_warehouses.map(warehouse => (
                          <span key={warehouse} className="tag warehouse-tag">
                            {warehouse}
                          </span>
                        ))}
                        {insight.affected_weeks.slice(0, 3).map(week => (
                          <span key={week} className="tag week-tag">
                            {week}
                          </span>
                        ))}
                        {insight.affected_weeks.length > 3 && (
                          <span className="tag more-tag">
                            +{insight.affected_weeks.length - 3} more weeks
                          </span>
                        )}
                      </div>
                    </div>

                    {insight.recommendations.length > 0 && (
                      <div className="recommendations">
                        <strong>Recommendations:</strong>
                        <ul>
                          {insight.recommendations.slice(0, 3).map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                          {insight.recommendations.length > 3 && (
                            <li>... and {insight.recommendations.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="insight-footer">
                    <span className="insight-timestamp">
                      {new Date(insight.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="insights-actions">
        <button 
          onClick={loadInsights}
          className="btn btn-primary"
        >
          Refresh Insights
        </button>
        <button 
          onClick={() => {
            // In real implementation, this would trigger AI analysis
            alert('AI analysis triggered! New insights will appear shortly.');
          }}
          className="btn btn-secondary"
        >
          Generate New Insights
        </button>
      </div>
    </div>
  );
};

export default InsightsDashboard;